/**
 * PackagesOutstanding — Phase A3 (cockpit half)
 *
 * Sold-but-not-fully-consumed packs. Powers the merchant's view of
 * deferred-revenue + expiring-soon (the 14-day banner the design called
 * out). Per-row actions: open RefundDialog; Nudge LINE/Extend expiry
 * land with B-series + A4b respectively.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2 } from 'lucide-react'

import { billing, type PackagePurchase } from '../api/client'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { RefundDialog } from './RefundDialog'

function fmtMoney(minor: number, currency: string, locale: string): string {
  const major = minor / 100
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(major)
  } catch {
    return `${currency} ${major}`
  }
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

function daysUntil(iso: string | null): number {
  if (!iso) return Infinity
  const diff = new Date(iso).getTime() - Date.now()
  return Math.floor(diff / 86_400_000)
}

export function PackagesOutstanding() {
  const { t, i18n } = useTranslation()
  const isZh = i18n.language?.toLowerCase().startsWith('zh')
  const [items, setItems] = useState<PackagePurchase[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reload, setReload] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [refundingFor, setRefundingFor] = useState<PackagePurchase | null>(null)

  useEffect(() => {
    let alive = true
    setItems(null)
    setError(null)
    billing
      .listPackagePurchases(
        statusFilter === 'all' ? {} : { status: statusFilter },
      )
      .then((r) => alive && setItems(r.items))
      .catch(() => alive && setError('load_failed'))
    return () => {
      alive = false
    }
  }, [statusFilter, reload])

  const summary = useMemo(() => {
    if (!items) return null
    const expiring = items.filter(
      (p) => p.expires_at && daysUntil(p.expires_at) <= 14 && p.status === 'active',
    )
    const totalFaceValue = items.reduce(
      (s, p) => s + p.amount_paid_minor - p.amount_refunded_minor,
      0,
    )
    return { expiring: expiring.length, totalFaceValue }
  }, [items])

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>{t('packages.outstanding.title')}</CardTitle>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded border px-2 text-sm"
              data-testid="outstanding-status-filter"
            >
              <option value="all">{t('packages.outstanding.statusAll')}</option>
              <option value="active">{t('packages.outstanding.statusActive')}</option>
              <option value="exhausted">{t('packages.outstanding.statusExhausted')}</option>
              <option value="refunded">{t('packages.outstanding.statusRefunded')}</option>
            </select>
          </div>
          {summary && (
            <CardDescription>
              {t('packages.outstanding.summary', {
                count: summary.expiring,
                total: fmtMoney(summary.totalFaceValue, 'TWD', i18n.language),
              })}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {items === null && !error && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('packages.loading')}
            </div>
          )}
          {error && (
            <p className="text-sm text-destructive">
              {t('packages.errors.loadFailed')}
            </p>
          )}
          {summary && summary.expiring > 0 && (
            <div
              className="mb-4 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="expiring-banner"
            >
              <AlertTriangle className="h-4 w-4" />
              {t('packages.outstanding.expiringBanner', { count: summary.expiring })}
            </div>
          )}
          {items && items.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('packages.outstanding.empty')}
            </p>
          )}
          {items && items.length > 0 && (
            <div className="-mx-4 overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2">{t('packages.outstanding.colPackage')}</th>
                    <th className="px-4 py-2">{t('packages.outstanding.colRemaining')}</th>
                    <th className="px-4 py-2">{t('packages.outstanding.colPaid')}</th>
                    <th className="px-4 py-2">{t('packages.outstanding.colExpires')}</th>
                    <th className="px-4 py-2">{t('packages.outstanding.colStatus')}</th>
                    <th className="px-4 py-2 text-right">{t('packages.outstanding.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => {
                    const days = daysUntil(p.expires_at)
                    const expiringSoon = p.status === 'active' && days <= 14
                    return (
                      <tr
                        key={p.id}
                        className="border-b last:border-0"
                        data-testid={`purchase-row-${p.id}`}
                      >
                        <td className="px-4 py-2">
                          <div className="font-medium">
                            {isZh ? p.snapshot_name_zh : p.snapshot_name_en}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t(`packages.serviceType.${p.snapshot_service_type}`)}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          {p.sessions_remaining} / {p.sessions_total}
                        </td>
                        <td className="px-4 py-2">
                          {fmtMoney(p.amount_paid_minor, p.currency, i18n.language)}
                          {p.amount_refunded_minor > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {t('packages.outstanding.refundedAmount', {
                                amount: fmtMoney(
                                  p.amount_refunded_minor,
                                  p.currency,
                                  i18n.language,
                                ),
                              })}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {fmtDate(p.expires_at)}
                          {expiringSoon && (
                            <span className="ml-2 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                              {t('packages.outstanding.daysLeft', { days })}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-xs ${
                              p.status === 'active'
                                ? 'bg-emerald-100 text-emerald-900'
                                : p.status === 'refunded'
                                  ? 'bg-amber-100 text-amber-900'
                                  : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {t(`packages.outstanding.status_${p.status}`, p.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          {p.status === 'active' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setRefundingFor(p)}
                              data-testid={`refund-${p.id}`}
                            >
                              {t('packages.outstanding.refundBtn')}
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <RefundDialog
        open={!!refundingFor}
        onOpenChange={(open) => {
          if (!open) setRefundingFor(null)
        }}
        purchase={refundingFor}
        onRefunded={() => {
          setRefundingFor(null)
          setReload((n) => n + 1)
        }}
      />
    </div>
  )
}
